import React, { useEffect } from 'react';
import { styled } from '@components-shared/src/stitches';
import { ChipContainer, RestyledScrollArea } from '@components/Feed/shared';
import {
  ScrollViewport,
  StyledScrollbar,
  StyledThumb,
} from '@components/main-components/containers/RadixScroll';
import { Chip } from '@components/main-components/elements/chips/Chip';
import {
  ArtistActivityFeedActivityTypeFilterOption,
  ArtistActivityFeedTypeFilterOption,
} from '@graphql/reactQuery';
import { multiSelectOptionIsSelected, selectMultiSelectOption } from '@utils/helpers';

type FeedType = {
  type: ArtistActivityFeedActivityTypeFilterOption;
  label: string;
  icon?: React.ReactNode;
}[];

const FEED_TYPES = (): FeedType => [
  {
    type: ArtistActivityFeedActivityTypeFilterOption.All,
    label: 'All',
  },
  {
    type: ArtistActivityFeedActivityTypeFilterOption.Posts,
    label: 'Posts',
  },
  {
    type: ArtistActivityFeedActivityTypeFilterOption.Collections,
    label: 'Collected',
  },
  {
    type: ArtistActivityFeedActivityTypeFilterOption.Playlists,
    label: 'Playlists',
  },
  {
    type: ArtistActivityFeedActivityTypeFilterOption.Releases,
    label: 'Releases',
  },
  {
    type: ArtistActivityFeedActivityTypeFilterOption.Likes,
    label: 'Likes',
  },
];

type UserType = {
  type: ArtistActivityFeedTypeFilterOption;
  label: string;
}[];

const FEED_USER_TYPES: UserType = [
  {
    type: ArtistActivityFeedTypeFilterOption.All,
    label: 'All',
  },
  {
    type: ArtistActivityFeedTypeFilterOption.Artist,
    label: 'Artist',
  },
  {
    type: ArtistActivityFeedTypeFilterOption.Collector,
    label: 'Collectors',
  },
];

export const FeedHeader = ({
  activityFilter,
  setActivityFilter,
  userFilter,
  setUserFilter,
}: {
  activityFilter: ArtistActivityFeedActivityTypeFilterOption[];
  setActivityFilter: (selection: ArtistActivityFeedActivityTypeFilterOption[]) => void;
  userFilter: ArtistActivityFeedTypeFilterOption[];
  setUserFilter: (selection: ArtistActivityFeedTypeFilterOption[]) => void;
}) => {
  // Set back activity type filter to all if user filter is set to collector
  // Collectors don't have releases
  useEffect(() => {
    if (
      userFilter.length === 1 &&
      userFilter[0] === ArtistActivityFeedTypeFilterOption.Collector &&
      activityFilter.length === 1 &&
      activityFilter[0] === ArtistActivityFeedActivityTypeFilterOption.Releases
    ) {
      setActivityFilter([ArtistActivityFeedActivityTypeFilterOption.All]);
    }
  }, [activityFilter, setActivityFilter, userFilter]);

  return (
    <Container>
      <Wrapper>
        <Filter>
          <h3>Types</h3>
          <RestyledScrollArea>
            <ScrollViewport>
              <FilterContainer>
                {FEED_TYPES().map(({ type, label }) => (
                  <ChipContainer key={type}>
                    <Chip
                      text={label}
                      isSelected={multiSelectOptionIsSelected({
                        prev: activityFilter || [],
                        currentSelected: type,
                        all: Object.values(ArtistActivityFeedActivityTypeFilterOption),
                        allKey: ArtistActivityFeedActivityTypeFilterOption.All,
                      })}
                      onClick={() => {
                        setActivityFilter(
                          selectMultiSelectOption({
                            prev: activityFilter || [],
                            currentSelected: type,
                            all: Object.values(ArtistActivityFeedActivityTypeFilterOption),
                            allKey: ArtistActivityFeedActivityTypeFilterOption.All,
                          }),
                        );
                      }}
                      // Collectors have no releases, let's disable the releases
                      // filter if collectors is selected
                      disabled={
                        userFilter.length === 1 &&
                        userFilter[0] === ArtistActivityFeedTypeFilterOption.Collector &&
                        type === ArtistActivityFeedActivityTypeFilterOption.Releases
                      }
                    />
                  </ChipContainer>
                ))}
              </FilterContainer>
            </ScrollViewport>
            <StyledScrollbar orientation="horizontal" hiddenOnMobile>
              <StyledThumb />
            </StyledScrollbar>
          </RestyledScrollArea>
        </Filter>

        <Filter>
          <h3>Users</h3>
          <RestyledScrollArea>
            <ScrollViewport>
              <FilterContainer>
                {FEED_USER_TYPES.map(({ type, label }) => (
                  <ChipContainer key={type}>
                    <Chip
                      text={label}
                      isSelected={multiSelectOptionIsSelected({
                        prev: userFilter || [],
                        currentSelected: type,
                        all: Object.values(ArtistActivityFeedTypeFilterOption),
                        allKey: ArtistActivityFeedTypeFilterOption.All,
                      })}
                      onClick={() => {
                        setUserFilter(
                          selectMultiSelectOption({
                            prev: userFilter || [],
                            currentSelected: type,
                            all: Object.values(ArtistActivityFeedTypeFilterOption),
                            allKey: ArtistActivityFeedTypeFilterOption.All,
                          }),
                        );
                      }}
                      // Collectors have no releases, let's disable collectors
                      // filter if releases is selected
                      disabled={
                        activityFilter.length === 1 &&
                        activityFilter[0] === ArtistActivityFeedActivityTypeFilterOption.Releases &&
                        type === ArtistActivityFeedTypeFilterOption.Collector
                      }
                    />
                  </ChipContainer>
                ))}
              </FilterContainer>
            </ScrollViewport>
            <StyledScrollbar orientation="horizontal" hiddenOnMobile>
              <StyledThumb />
            </StyledScrollbar>
          </RestyledScrollArea>
        </Filter>
      </Wrapper>
    </Container>
  );
};
const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 16,
  paddingTop: 20,

  '@tablet': {
    gap: 20,
  },
});

const FilterContainer = styled('div', {
  display: 'flex',
  width: '100%',
  paddingBottom: 0,
  paddingLeft: 24,
});

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,

  '@tablet': {
    gap: 20,
  },

  '@laptop': {
    paddingLeft: 0,
  },
});

const Filter = styled('div', {
  display: 'flex',
  flexDirection: 'column',

  h3: {
    kicker: 'S',
    fontStyle: 'regular',
    textTransform: 'uppercase',
    paddingLeft: 24,

    '@laptop': {
      paddingLeft: 0,
    },
  },

  '@tablet': {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
